/**
 * 학생 상세 — 학생을 고른 오른쪽 칸(설계 §4).
 *
 * 머리는 학습 v2(StudentLearningPanel.jsx:303-367)와 같은 자리 · 같은 모양 — 왼쪽 단추 · 학생 카드(32px 번호 원 · 이름 ·
 * 학교와 학년 · 오른쪽 작은 숫자 셋) · 오른쪽 90px 「닫기」. 왼쪽 단추는 매니저 [작업 ▾](쓰기 진입점, 설계 §4-2) ·
 * 강사 [메모 쓰기](강사 쓰기는 메모와 본인 반 출결뿐, 요청서 §10).
 * 번호 원은 목록과 같은 SID(student_id)다. 출결번호는 본문의 학생 정보에서 별도로 보인다.
 *
 * 본문은 탭(설계 D3) — 개요 · 반 편성 · 출결 · 학교·부서 · 메모 · 이력. 탭은 주소 ?tab= 가 정본이다(부모가 넘김).
 * 쓰기 창은 이 패널이 연다 — 개요 카드 · 작업 메뉴 · 반 편성 줄 · 학교·부서 줄 · 출결 미입력 줄. 저장하면 다시 부른다.
 * 서버에 아직 없는 작업은 메뉴에서 「n단계에 열림」으로 잠그고, 출결 탭은 안내만 선다(stageGate · 백엔드 2단계 전달 §4-F).
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDesktopExpanded } from '../../hooks/useDesktopExpanded';
import { Button } from '../common';
import { getStudent } from './academyApi';
import { totalTenureDays } from './academyModel';
import { todayKST } from './periodDates';
import { lockedStage } from './stageGate';
import { ErrorNotice, LockedNotice, MenuButton, SegTabs, Spinner, StateBadge, ToneBadge, useAcademyVersion, useAsync } from './ui';
import { AttendanceTab, EnrollmentsTab, HistoryTab, NotesTab, OverviewTab, SchoolTab } from './detail/DetailTabs';
import StudentActionModal, { ACTION_LABEL, classActions, stateActions } from './modals/StudentActionModal';
import AttendanceModal from './modals/AttendanceModal';
import StudentIdBadge from './StudentIdBadge';

const DETAIL_TABS = [['overview', '개요'], ['enroll', '반 편성'], ['attendance', '출결'], ['school', '학교·부서'], ['notes', '메모'], ['history', '이력']];

function StudentDetailPanel({ studentId, isTeacher = false, myStaffId = null, tab = 'overview', onTabChange, onClose }) {
  const { getAccessToken } = useAuth();
  const { isMobileView } = useDesktopExpanded();
  const version = useAcademyVersion();
  const res = useAsync((signal) => getStudent(studentId, { signal }, getAccessToken), `student-${studentId}-${version}`);
  const detail = res.data;
  const readOnly = isTeacher || !!detail?.profile.is_deleted;
  const asOf = detail?.as_of || todayKST();
  const active = DETAIL_TABS.some(([k]) => k === tab) ? tab : 'overview';
  const outOfScope = res.error?.reason === 'out_of_scope';
  const [modal, setModal] = useState(null);

  // 메뉴·카드는 같은 작업 묶음을 연다. 메뉴는 누른 작업(initial)을 미리 고르고, 행 작업은 선택한 기록을 그대로 받는다.
  const onAction = (key, opts = {}) => {
    if (!detail || readOnly) return;
    const actions = key === 'state' ? stateActions(detail) : key === 'class' ? classActions(detail) : [key];
    setModal({ ...opts, actions });
  };
  // 출결 입력 — 매니저는 모든 반, 강사는 오늘 담임인 반만(요청서 §10 판 4.1)
  const canInputClass = (classId) => !detail?.profile.is_deleted && (!isTeacher || detail?.current.classes.some((c) => c.class_id === classId && c.teacher?.staff_id === myStaffId));
  const menuItem = (a, group) => ({ key: a, label: ACTION_LABEL[a], danger: a === 'withdraw', onSelect: () => onAction(group, { initial: a }) });
  const menuItems = detail ? [
    // 메뉴에는 상태를 바꾸는 진입점만 둔다. 정정·삭제는 공통 상태 창이나 기록 행에서 고른다.
    ...stateActions(detail).filter((a) => !/^(correct|delete)/.test(a)).map((a) => menuItem(a, 'state')),
    { key: 'profile', label: ACTION_LABEL.profile, onSelect: () => onAction('profile') },
    ...(classActions(detail).length ? [{ key: 'd1', divider: true }] : []),
    ...classActions(detail).map((a) => menuItem(a, 'class')),
    { key: 'd2', divider: true },
    { key: 'school', label: ACTION_LABEL.school, onSelect: () => onAction('school') },
    { key: 'department', label: ACTION_LABEL.department, onSelect: () => onAction('department') },
    { key: 'd3', divider: true },
    { key: 'attendance', label: '출결 입력', lockedAt: lockedStage('attendance'), onSelect: () => onTabChange('attendance') },
    { key: 'notes', label: '메모 쓰기', onSelect: () => onTabChange('notes') },
  ] : [];

  return (
    <div className="flex size-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      {/* 머리 */}
      <div className="shrink-0 border-b">
        <div className={`w-full ${isMobileView ? 'px-3 py-2' : 'px-4 py-3'}`}>
          <div className="flex items-center justify-between gap-3">
            {detail && !readOnly && (
              <MenuButton
                label="작업"
                items={menuItems}
                prominent
              />
            )}
            {detail && isTeacher && !detail.profile.is_deleted && (
              <Button variant="primary" size="sm" onClick={() => onTabChange('notes')} className="shrink-0">메모 쓰기</Button>
            )}
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 rounded-3xl border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
              <StudentIdBadge
                id={detail?.student_id ?? studentId}
                blocked={detail?.profile.is_blocked}
              />
              <div className="min-w-0 flex-1">
                <p className="flex min-w-0 flex-wrap items-center gap-1.5 text-sm font-semibold text-gray-900">
                  <span className="truncate">{detail?.student_name || '학생'}</span>
                  {detail?.profile.is_test && <ToneBadge>테스트</ToneBadge>}
                  {detail?.profile.is_deleted && <ToneBadge>삭제된 계정</ToneBadge>}
                  {detail && <StateBadge row={detail.current} />}
                  {detail?.profile.is_blocked && <ToneBadge tone="danger" title="비밀번호를 새로 정하면 풀립니다">로그인 잠김</ToneBadge>}
                </p>
                <p className="truncate text-xs text-gray-500">
                  {detail ? `${detail.current.school?.name || '학교 미지정'} ${detail.current.grade_label} · ${detail.current.department?.name || '부서 없음'}` : ' '}
                </p>
              </div>
              {detail && (
                <div className="flex w-full shrink-0 justify-end gap-3 text-sm sm:w-auto">
                  <div className="text-center" title="재원 기간마다 오늘까지 센 날의 합">
                    <p className="text-[10px] text-gray-500">재원 일수</p>
                    <p className="text-xs font-bold text-gray-900">{totalTenureDays(detail.tenures, asOf)}</p>
                  </div>
                  <div className="text-center" title="재원 기간 수 — 2 이상이면 재등록">
                    <p className="text-[10px] text-gray-500">등록</p>
                    <p className="text-xs font-bold text-primary">{detail.tenures.length}회</p>
                  </div>
                  <div className="text-center" title="오늘 편성된 반 수">
                    <p className="text-[10px] text-gray-500">수강반</p>
                    <p className="text-xs font-bold text-gray-900">{detail.current.classes.length}</p>
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="flex shrink-0 items-center justify-center gap-1"
              style={{ width: isMobileView ? undefined : '90px' }}
              aria-label="닫기"
            >
              <X className="size-4" />
              {!isMobileView && '닫기'}
            </Button>
          </div>
        </div>
      </div>

      {/* 본문 */}
      <div className={`min-h-0 flex-1 overflow-y-auto ${isMobileView ? 'p-3' : 'p-4'}`}>
        {res.loading && !detail && <Spinner label="학생 정보 불러오는 중" />}
        {outOfScope && (
          <div role="status" className="py-12 text-center text-sm text-gray-500">
            담당 반 학생이 아닙니다.
            <div className="mt-3"><Button variant="secondary" size="sm" onClick={onClose}>목록으로</Button></div>
          </div>
        )}
        {res.error && !outOfScope && <ErrorNotice error={res.error} onRetry={res.reload} />}
        {detail && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <SegTabs tabs={DETAIL_TABS} value={active} onChange={onTabChange} ariaLabel="학생 상세 탭" />
              <span className="ml-auto text-xs text-gray-500">{asOf} 기준</span>
            </div>
            {active === 'overview' && <OverviewTab detail={detail} asOf={asOf} onAction={readOnly ? undefined : onAction} />}
            {active === 'enroll' && <EnrollmentsTab detail={detail} asOf={asOf} onAction={readOnly ? undefined : onAction} />}
            {active === 'attendance' && (lockedStage('attendance')
              ? <LockedNotice feature="attendance" what="출결" />
              : <AttendanceTab detail={detail} canInput={canInputClass} onInput={(it) => setModal({ attendance: { classId: it.class_id, className: it.class_name, date: it.date, startTime: it.start_time } })} />)}
            {active === 'school' && <SchoolTab detail={detail} onAction={readOnly ? undefined : onAction} />}
            {active === 'notes' && <NotesTab detail={detail} isTeacher={isTeacher} myStaffId={myStaffId} />}
            {active === 'history' && <HistoryTab detail={detail} isTeacher={isTeacher} />}
          </div>
        )}
      </div>

      {detail && modal?.actions?.length > 0 && (
        <StudentActionModal detail={detail} actions={modal.actions} initial={modal.initial} kind={modal.kind} preset={modal.preset} rows={modal.rows} onClose={() => setModal(null)} />
      )}
      {modal?.attendance && <AttendanceModal {...modal.attendance} onClose={() => setModal(null)} />}
    </div>
  );
}

export default StudentDetailPanel;
